import type {
  PresentationObjectRegion,
  PresentationObjectSelectionDescriptor,
  PresentationPlaceholderRole,
  PresentationSelectionState,
  PresentationTextSelectionDescriptor,
} from '@/components/office/PresentationViewer/viewerTypes'

const PRESENTATION_COMPONENT_SELECTOR = '[data-presentation-component]'
const LEGACY_NODE_ID_PREFIX = 'legacy:'

const EMPTY_OBJECT_SELECTIONS: readonly PresentationObjectSelectionDescriptor[] =
  Object.freeze([])

export const EMPTY_PRESENTATION_SELECTION: PresentationSelectionState =
  Object.freeze({
    objects: EMPTY_OBJECT_SELECTIONS,
  })

export const presentationObjectSelectionKey = (
  descriptor: PresentationObjectSelectionDescriptor,
) =>
  JSON.stringify([
    descriptor.slideIndex,
    descriptor.nodeSource,
    descriptor.nodeKind,
    descriptor.nodeId,
  ])

const textSelectionsEqual = (
  left: PresentationTextSelectionDescriptor | undefined,
  right: PresentationTextSelectionDescriptor | undefined,
) =>
  left === right ||
  (left != null &&
    right != null &&
    left.slideIndex === right.slideIndex &&
    left.nodeId === right.nodeId &&
    left.nodeKind === right.nodeKind &&
    left.nodeSource === right.nodeSource &&
    (left.direction ?? 'forward') === (right.direction ?? 'forward') &&
    left.startOffset === right.startOffset &&
    left.endOffset === right.endOffset &&
    left.text === right.text)

export const presentationSelectionsEqual = (
  left: PresentationSelectionState,
  right: PresentationSelectionState,
) => {
  if (
    left === right ||
    (left.objects === right.objects && left.text === right.text)
  ) {
    return true
  }
  if (
    left.objects.length !== right.objects.length ||
    !textSelectionsEqual(left.text, right.text)
  ) {
    return false
  }
  return left.objects.every(
    (descriptor, index) =>
      presentationObjectSelectionKey(descriptor) ===
      presentationObjectSelectionKey(right.objects[index]!),
  )
}

export const normalizePresentationSelection = (
  selection: PresentationSelectionState,
): PresentationSelectionState => {
  const seen = new Set<string>()
  const objects = selection.objects.filter((descriptor) => {
    const key = presentationObjectSelectionKey(descriptor)
    if (seen.has(key)) {
      return false
    }
    seen.add(key)
    return true
  })
  return {
    objects,
    ...(selection.text ? { text: { ...selection.text } } : {}),
  }
}

const legacyNodePath = (element: Element, slideRoot: HTMLElement) => {
  const indices: number[] = []
  let current: Element | null = element
  while (current && current !== slideRoot) {
    const parent: Element | null = current.parentElement
    if (!parent) {
      return null
    }
    const index = Array.prototype.indexOf.call(parent.children, current)
    if (index < 0) {
      return null
    }
    indices.unshift(index)
    current = parent
  }
  return current === slideRoot
    ? `${LEGACY_NODE_ID_PREFIX}${indices.join('.')}`
    : null
}

const elementAttribute = (element: Element, name: string) =>
  element.getAttribute(name)?.trim() || undefined

// Mirrors the per-object clamp in the backend viewport context model
// (VIEWPORT_SELECTED_OBJECT_TEXT_MAX_CHARS); keeps marquee selections over
// many text-bearing shapes from ballooning the wire payload.
const OBJECT_TEXT_SNIPPET_MAX_CHARS = 80

const objectTextSnippet = (element: Element) => {
  const text = element.textContent?.replace(/\s+/g, ' ').trim()
  if (!text) return undefined
  return text.length > OBJECT_TEXT_SNIPPET_MAX_CHARS
    ? `${text.slice(0, OBJECT_TEXT_SNIPPET_MAX_CHARS - 1)}…`
    : text
}

// Thirds of the slide in each axis; a bounding box's center picks the cell.
const objectRegion = (
  element: Element,
  slideRoot: HTMLElement,
): PresentationObjectRegion | undefined => {
  const rootRect = slideRoot.getBoundingClientRect()
  if (rootRect.width <= 0 || rootRect.height <= 0) return undefined
  const rect = element.getBoundingClientRect()
  if (rect.width <= 0 && rect.height <= 0) return undefined
  const x = (rect.left + rect.width / 2 - rootRect.left) / rootRect.width
  const y = (rect.top + rect.height / 2 - rootRect.top) / rootRect.height
  const column = x < 1 / 3 ? 'left' : x > 2 / 3 ? 'right' : 'center'
  const row = y < 1 / 3 ? 'top' : y > 2 / 3 ? 'bottom' : 'center'
  if (row === 'center') return column === 'center' ? 'center' : column
  if (column === 'center') return row
  return `${row}-${column}`
}

const PLACEHOLDER_ROLES: readonly PresentationPlaceholderRole[] = [
  'body',
  'date',
  'footer',
  'header',
  'object',
  'slide_number',
  'subtitle',
  'title',
]

const placeholderRoleAttribute = (
  element: Element,
): PresentationPlaceholderRole | undefined => {
  const value = elementAttribute(element, 'data-presentation-placeholder-role')
  return PLACEHOLDER_ROLES.find((role) => role === value)
}

export const presentationObjectSelectionFromElement = (
  element: Element,
  slideRoot: HTMLElement,
  slideIndex: number,
): PresentationObjectSelectionDescriptor | null => {
  if (!slideRoot.contains(element)) {
    return null
  }
  const nodeId =
    elementAttribute(element, 'data-presentation-node-id') ??
    elementAttribute(element, 'data-node-id') ??
    legacyNodePath(element, slideRoot)
  if (!nodeId) {
    return null
  }
  const text = objectTextSnippet(element)
  const region = objectRegion(element, slideRoot)
  const role = placeholderRoleAttribute(element)
  return {
    nodeId,
    nodeKind:
      elementAttribute(element, 'data-presentation-node-kind') ??
      elementAttribute(element, 'data-presentation-component') ??
      element.tagName.toLowerCase(),
    nodeSource:
      elementAttribute(element, 'data-presentation-node-source') ?? 'slide',
    slideIndex,
    ...(text !== undefined ? { text } : {}),
    ...(region !== undefined ? { region } : {}),
    ...(role !== undefined ? { role } : {}),
  }
}

const resolveLegacyNodePath = (
  slideRoot: HTMLElement,
  nodeId: string,
): Element | null => {
  if (!nodeId.startsWith(LEGACY_NODE_ID_PREFIX)) {
    return null
  }
  const path = nodeId.slice(LEGACY_NODE_ID_PREFIX.length)
  if (!path) {
    return slideRoot
  }
  let current: Element = slideRoot
  for (const segment of path.split('.')) {
    const index = Number.parseInt(segment, 10)
    const next: Element | undefined = current.children[index]
    if (!Number.isInteger(index) || index < 0 || !next) {
      return null
    }
    current = next
  }
  return current
}

export const resolvePresentationObjectSelection = (
  slideRoot: HTMLElement,
  descriptor: PresentationObjectSelectionDescriptor,
): Element | null =>
  resolvePresentationObjectSelections(slideRoot, descriptor.slideIndex, [
    descriptor,
  ])[0] ?? null

export const resolvePresentationObjectSelections = (
  slideRoot: HTMLElement,
  slideIndex: number,
  descriptors: readonly PresentationObjectSelectionDescriptor[],
) => {
  const resolved = new Map<string, Element>()
  const nativeDescriptorKeys = new Set(
    descriptors
      .filter(
        (descriptor) =>
          descriptor.slideIndex === slideIndex &&
          !descriptor.nodeId.startsWith(LEGACY_NODE_ID_PREFIX),
      )
      .map(presentationObjectSelectionKey),
  )
  if (nativeDescriptorKeys.size > 0) {
    for (const candidate of slideRoot.querySelectorAll(
      '[data-presentation-node-id], [data-node-id]',
    )) {
      const candidateDescriptor = presentationObjectSelectionFromElement(
        candidate,
        slideRoot,
        slideIndex,
      )
      if (!candidateDescriptor) {
        continue
      }
      const key = presentationObjectSelectionKey(candidateDescriptor)
      if (nativeDescriptorKeys.has(key) && !resolved.has(key)) {
        resolved.set(key, candidate)
      }
    }
  }

  return descriptors.map((descriptor) =>
    descriptor.slideIndex !== slideIndex
      ? null
      : descriptor.nodeId.startsWith(LEGACY_NODE_ID_PREFIX)
        ? resolveLegacyNodePath(slideRoot, descriptor.nodeId)
        : (resolved.get(presentationObjectSelectionKey(descriptor)) ?? null),
  )
}

const closestPresentationComponent = (node: Node, slideRoot: HTMLElement) => {
  const element = node instanceof Element ? node : node.parentElement
  const component = element?.closest(PRESENTATION_COMPONENT_SELECTOR)
  return component && slideRoot.contains(component) ? component : null
}

const characterOffsetWithin = (
  container: Element,
  node: Node,
  offset: number,
) => {
  const range = container.ownerDocument.createRange()
  range.selectNodeContents(container)
  range.setEnd(node, offset)
  return range.toString().length
}

const nativeSelectionDirection = (
  selection: Selection,
  range: Range,
): 'backward' | 'forward' =>
  selection.anchorNode === range.endContainer &&
  selection.anchorOffset === range.endOffset &&
  selection.focusNode === range.startContainer &&
  selection.focusOffset === range.startOffset
    ? 'backward'
    : 'forward'

const clampRangeToElement = (range: Range, element: Element): Range => {
  const container = element.ownerDocument.createRange()
  container.selectNodeContents(element)
  const clamped = range.cloneRange()
  if (clamped.compareBoundaryPoints(Range.START_TO_START, container) < 0) {
    clamped.setStart(container.startContainer, container.startOffset)
  }
  if (clamped.compareBoundaryPoints(Range.END_TO_END, container) > 0) {
    clamped.setEnd(container.endContainer, container.endOffset)
  }
  return clamped
}

export const capturePresentationTextSelection = (
  slideRoot: HTMLElement,
  slideIndex: number,
  selection: Selection,
  clampTo?: Element | null,
): PresentationTextSelectionDescriptor | undefined => {
  if (selection.isCollapsed || selection.rangeCount === 0) {
    return undefined
  }
  const liveRange = selection.getRangeAt(0)
  // Browsers extend double/triple-click and drag selections past the last
  // character into the next block (often outside the component or even the
  // slide). Clamping to the interaction's component keeps the in-component
  // portion selectable instead of discarding the whole gesture.
  const direction = nativeSelectionDirection(selection, liveRange)
  const range = clampTo ? clampRangeToElement(liveRange, clampTo) : liveRange
  if (range.collapsed) {
    return undefined
  }
  if (
    !slideRoot.contains(range.startContainer) ||
    !slideRoot.contains(range.endContainer)
  ) {
    return undefined
  }
  const startComponent = closestPresentationComponent(
    range.startContainer,
    slideRoot,
  )
  const endComponent = closestPresentationComponent(
    range.endContainer,
    slideRoot,
  )
  const offsetContainer =
    startComponent && startComponent === endComponent
      ? startComponent
      : slideRoot
  const nodeDescriptor =
    offsetContainer === slideRoot
      ? null
      : presentationObjectSelectionFromElement(
          offsetContainer,
          slideRoot,
          slideIndex,
        )

  return {
    direction,
    endOffset: characterOffsetWithin(
      offsetContainer,
      range.endContainer,
      range.endOffset,
    ),
    ...(nodeDescriptor
      ? {
          nodeId: nodeDescriptor.nodeId,
          nodeKind: nodeDescriptor.nodeKind,
          nodeSource: nodeDescriptor.nodeSource,
        }
      : {}),
    slideIndex,
    startOffset: characterOffsetWithin(
      offsetContainer,
      range.startContainer,
      range.startOffset,
    ),
    text: range.toString(),
  }
}

interface TextPosition {
  node: Text
  offset: number
}

const textPositionAtOffset = (
  container: Element,
  requestedOffset: number,
): TextPosition | null => {
  const document = container.ownerDocument
  const showText = document.defaultView?.NodeFilter.SHOW_TEXT ?? 4
  const walker = document.createTreeWalker(container, showText)
  const offset = Math.max(0, requestedOffset)
  let consumed = 0
  let lastTextNode: Text | null = null
  while (walker.nextNode()) {
    const textNode = walker.currentNode as Text
    const length = textNode.data.length
    if (offset <= consumed + length) {
      return {
        node: textNode,
        offset: Math.min(length, offset - consumed),
      }
    }
    consumed += length
    lastTextNode = textNode
  }
  return lastTextNode && offset === consumed
    ? { node: lastTextNode, offset: lastTextNode.data.length }
    : null
}

export const restorePresentationTextSelection = (
  slideRoot: HTMLElement,
  descriptor: PresentationTextSelectionDescriptor,
) => {
  const offsetContainer = descriptor.nodeId
    ? resolvePresentationObjectSelection(slideRoot, {
        nodeId: descriptor.nodeId,
        nodeKind: descriptor.nodeKind ?? '',
        nodeSource: descriptor.nodeSource ?? '',
        slideIndex: descriptor.slideIndex,
      })
    : slideRoot
  if (!offsetContainer) {
    return false
  }
  const start = textPositionAtOffset(offsetContainer, descriptor.startOffset)
  const end = textPositionAtOffset(offsetContainer, descriptor.endOffset)
  if (!start || !end) {
    return false
  }

  const range = slideRoot.ownerDocument.createRange()
  range.setStart(start.node, start.offset)
  range.setEnd(end.node, end.offset)
  if (range.toString() !== descriptor.text) {
    return false
  }
  const selection = slideRoot.ownerDocument.defaultView?.getSelection()
  if (!selection) {
    return false
  }

  const direction = descriptor.direction ?? 'forward'
  if (selection.rangeCount === 1 && !selection.isCollapsed) {
    const currentRange = selection.getRangeAt(0)
    if (
      currentRange.startContainer === start.node &&
      currentRange.startOffset === start.offset &&
      currentRange.endContainer === end.node &&
      currentRange.endOffset === end.offset &&
      nativeSelectionDirection(selection, currentRange) === direction
    ) {
      // Selection changes are mirrored into React state. Avoid tearing down an
      // already-matching live range, which would reset the browser's active
      // drag anchor and make backward/upward text selection jump forward.
      return true
    }
  }

  selection.removeAllRanges()
  if (direction === 'backward') {
    try {
      selection.setBaseAndExtent(end.node, end.offset, start.node, start.offset)
      return true
    } catch {
      // Older WebViews may expose setBaseAndExtent without accepting SVG or
      // detached text nodes. The normalized forward range is still usable.
      selection.removeAllRanges()
    }
  }
  selection.addRange(range)
  return true
}
